/* Open Houses — Phase 2 iOS-style agenda view + Google Maps route builder.

Backend endpoints used:
  GET  /api/openhouses             - list with filters
  GET  /api/openhouses/stats       - {total, visited, geocoded, neighborhoods}
  GET  /api/openhouses/:id         - detail
  POST /api/openhouses/scan        - admin-only: kick off Apify scan
  PATCH /api/openhouses/:id        - toggle visited / notes
  POST /api/openhouses/geocode-missing - backfill lat/lng (background)
  POST /api/openhouses/route       - returns chunked Google Maps URLs

Frontend conventions:
  - api from "@/lib/api" (carries JWT via interceptor)
  - data-testid kebab-case
  - 44px minimum touch targets

Section components live in @/components/openhouses/.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Home,
  CheckCircle2,
  Route,
  X,
  Filter,
  EyeOff,
  Tag,
  Sparkles,
  List,
  Map as MapIcon,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import OpenHouseMap from "@/components/OpenHouseMap";
import ScanOpenHousesModal from "@/components/ScanOpenHousesModal";
import DaySection from "@/components/openhouses/DaySection";
import EmptyState from "@/components/openhouses/EmptyState";
import ScanJobBanner from "@/components/openhouses/ScanJobBanner";
import KpiTile from "@/components/openhouses/KpiTile";
import OpenHouseDetailDialog from "@/components/openhouses/OpenHouseDetailDialog";
import RouteDialog from "@/components/openhouses/RouteDialog";
import { todayIso, addDaysIso } from "@/components/openhouses/utils";

const POLL_MS = 4000;

export default function OpenHousesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [openHouses, setOpenHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    visited: 0,
    geocoded: 0,
    pending_geocode: 0,
    neighborhoods: [],
  });
  const [filters, setFilters] = useState({
    fromDate: todayIso(),
    toDate: addDaysIso(todayIso(), 3),
    neighborhood: "all",
    hideVisited: false,
    onlyUnmet: false,
    search: "",
  });
  const [selected, setSelected] = useState(new Set());
  // Selection order is what RouteDialog passes to Google Maps. We track it
  // alongside `selected` so the user can re-order in the modal.
  const [selectionOrder, setSelectionOrder] = useState([]);
  const [scanJob, setScanJob] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [routeDialog, setRouteDialog] = useState(false);
  const [detailId, setDetailId] = useState(null);
  // Agenda vs map toggle. Agenda is default.
  const [viewMode, setViewMode] = useState("agenda");
  // Route-planner day picker for Map view. Realtor.ca has a mix of
  // "open today at 2pm" and "open Sunday" listings — planning a route
  // is useless if half the pins are for a day the user isn't driving.
  // `mapDate` scopes the map to events on that ISO date only. Empty
  // string = show all days in the active filter range (fallback).
  const [mapDate, setMapDate] = useState(() => todayIso());
  // Current vs Past listings tab
  const [listingTab, setListingTab] = useState("current");
  // Route optimization state (Task — auto-optimize via DirectionsService).
  // - directionsResult holds the Google response so the map can render the
  //   polyline. Cleared whenever the user adds/removes a stop or hits Clear.
  // - optimizationState: 'none' | 'optimized' | 'manual'
  //   * none      : haven't run optimization yet (or selection changed)
  //   * optimized : DirectionsService.waypoint_order applied
  //   * manual    : user reordered after optimization, treat their order as truth
  const [directionsResult, setDirectionsResult] = useState(null);
  const [optimizationState, setOptimizationState] = useState("none");
  // Stale-listings purge state (Task 3)
  const [noEventCount, setNoEventCount] = useState(0);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeBusy, setPurgeBusy] = useState(false);
  // Geocoded home for the map's center marker
  const [homeCoords, setHomeCoords] = useState(null);

  // Minute-ticker so the Current/Past classification stays accurate as
  // time moves forward while the page is open. Without this, a 1-3pm
  // open house would still show under "Current" at 5pm because the
  // classification useMemo would never re-run. Ticks every 60s.
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNowTs(Date.now()), 60_000);
    return () => clearInterval(handle);
  }, []);

  // -- Loaders --
  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/openhouses/stats");
      setStats(data);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadNoEventCount = useCallback(async () => {
    try {
      const { data } = await api.get("/openhouses/no-events/count");
      setNoEventCount(data.count || 0);
    } catch (e) {
      // ignore — button just won't show
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch the full open-house set so the Current/Past tab counts
      // stay accurate regardless of which tab is active. Date filtering is
      // applied client-side in the `grouped` memo for the Current tab; the
      // Past tab intentionally ignores the date range and shows everything.
      const params = {};
      if (filters.neighborhood && filters.neighborhood !== "all") {
        params.neighborhood = filters.neighborhood;
      }
      if (filters.hideVisited) params.visited = false;
      if (filters.search) params.search = filters.search;
      const { data } = await api.get("/openhouses", { params });
      let rows = data || [];
      if (filters.onlyUnmet) {
        rows = rows.filter((oh) => !oh.realtor_id || !oh.met_in_person_at);
      }
      setOpenHouses(rows);
    } catch (e) {
      toast.error("Failed to load open houses", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [filters.neighborhood, filters.hideVisited, filters.search, filters.onlyUnmet]);

  useEffect(() => {
    load();
    loadStats();
    loadNoEventCount();
  }, [load, loadStats, loadNoEventCount]);

  // Geocode home address via Nominatim once, so the map can place a "start
  // point" marker. Saved address is plain text in Profile so we have to ask
  // Nominatim for lat/lng. We cache by address string in localStorage to
  // avoid repeated 1-req/sec hits while the user navigates around.
  useEffect(() => {
    const addr = (user?.home_address || "").trim();
    if (!addr) {
      setHomeCoords(null);
      return;
    }
    const cacheKey = `homeCoords:${addr}`;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (cached && typeof cached.lat === "number") {
        setHomeCoords(cached);
        return;
      }
    } catch (_) {}
    const ctrl = new AbortController();
    const url =
      "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" +
      encodeURIComponent(addr);
    fetch(url, {
      headers: { Accept: "application/json" },
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((rows) => {
        if (Array.isArray(rows) && rows.length > 0) {
          const c = { lat: parseFloat(rows[0].lat), lng: parseFloat(rows[0].lon) };
          if (!Number.isNaN(c.lat) && !Number.isNaN(c.lng)) {
            setHomeCoords(c);
            try {
              localStorage.setItem(cacheKey, JSON.stringify(c));
            } catch (_) {}
          }
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [user?.home_address]);

  // -- Poll running scan job --
  useEffect(() => {
    if (!scanJob || (scanJob.status !== "running" && scanJob.status !== "queued")) {
      setScanBusy(false);
      return undefined;
    }
    setScanBusy(true);
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/scrape-jobs/${scanJob.id}`);
        setScanJob(data);
        if (data.status !== "running" && data.status !== "queued") {
          setScanBusy(false);
          await Promise.all([load(), loadStats()]);
        }
      } catch (e) {
        // ignore
      }
    }, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poller is keyed on job id + status; the job object itself changes every tick
  }, [scanJob?.id, scanJob?.status, load, loadStats]);

  // -- Split open houses into current (future events) and past (all events passed or no events) --
  const { currentListings, pastListings } = useMemo(() => {
    // Auto-move rule (Feb 2026): an open house is "Current" only while
    // at least one of its events still has time on the clock — i.e.
    // `end_at` is strictly in the future. Once every event's end_at is
    // in the past, the listing flips to "Past". So a 1-3pm event is
    // Current at 2pm and Past at 3:01pm without user action.
    //
    // No-event listings (newly-scanned, schedule not yet parsed) stay
    // on Current so the user can still open/geocode them.
    const now = new Date(nowTs);
    const current = [];
    const past = [];
    for (const oh of openHouses) {
      const events = Array.isArray(oh.events) ? oh.events : [];
      if (events.length === 0) {
        current.push(oh);
        continue;
      }
      const hasLiveOrFutureEvent = events.some((ev) => {
        try {
          return new Date(ev.end_at) > now;
        } catch {
          return false;
        }
      });
      if (hasLiveOrFutureEvent) current.push(oh);
      else past.push(oh);
    }
    return { currentListings: current, pastListings: past };
  }, [openHouses, nowTs]);

  // Select which set to display based on the tab
  const displayedOpenHouses = listingTab === "past" ? pastListings : currentListings;

  // Map-only day scoping — apply on top of `displayedOpenHouses` so
  // agenda view is unaffected. When `mapDate` is set, keep only rows
  // with at least one event whose ISO date matches. When empty, show
  // everything the agenda would show. Listings without any parsed
  // events fall through in either mode so pins for "schedule not
  // parsed" listings still render.
  const mapFilteredOpenHouses = useMemo(() => {
    if (!mapDate) return displayedOpenHouses;
    return displayedOpenHouses.filter((oh) => {
      const evs = Array.isArray(oh.events) ? oh.events : [];
      if (evs.length === 0) return false;  // hide no-event pins on a specific day
      return evs.some((ev) => (ev.start_at || "").slice(0, 10) === mapDate);
    });
  }, [displayedOpenHouses, mapDate]);

  // -- Per-day breakdown of the "Upcoming" tile. Counts each current listing
  // once against the earliest event that falls inside the active date filter
  // (fromDate..toDate). Days without events are still shown so the row lines
  // up with the filter range. Labels: "Today" / "Tomorrow" / weekday name.
  const dayBreakdown = useMemo(() => {
    const from = filters.fromDate || todayIso();
    const to = filters.toDate || from;
    if (from > to) return [];
    // Enumerate ISO days in [from..to] inclusive, cap at 7 days.
    const days = [];
    let cursor = from;
    while (cursor <= to && days.length < 7) {
      days.push(cursor);
      cursor = addDaysIso(cursor, 1);
    }
    const counts = new Map(days.map((d) => [d, 0]));
    for (const oh of currentListings) {
      const evs = Array.isArray(oh.events) ? oh.events : [];
      const inRange = evs
        .map((e) => (e.start_at ? e.start_at.slice(0, 10) : null))
        .filter((d) => d && counts.has(d))
        .sort();
      if (inRange.length > 0) counts.set(inRange[0], counts.get(inRange[0]) + 1);
    }
    const today = todayIso();
    const tomorrow = addDaysIso(today, 1);
    return days.map((d) => {
      let label;
      if (d === today) label = "Today";
      else if (d === tomorrow) label = "Tomorrow";
      else label = new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "long" });
      return { key: d, label, count: counts.get(d) || 0 };
    });
  }, [currentListings, filters.fromDate, filters.toDate]);

  // -- Group: when we have any events, use agenda mode but ALSO show
  // listings without events used to be grouped under a "Schedule not parsed"
  // bucket, but that section wasn't useful. We now simply exclude them from
  // the agenda; users can still bulk-clean them via the "Remove N stale"
  // button in the header (which uses the /openhouses/no-events/count endpoint).
  const grouped = useMemo(() => {
    const nowDate = new Date(nowTs);
    const haveEvents = displayedOpenHouses.some((oh) => Array.isArray(oh.events) && oh.events.length > 0);
    if (haveEvents) {
      const byDay = new Map();
      for (const oh of displayedOpenHouses) {
        const evs = (oh.events || [])
          .filter((e) => {
            // Skip date filtering for the Past tab — show all past events
            if (listingTab === "past") return true;
            // On the Current tab, hide any session whose end time has
            // already passed. Without this, a listing with Sat 1-3pm +
            // Sun 1-3pm sessions still shows the Saturday session under
            // "Today" at 5pm Saturday, because the whole listing is
            // Current thanks to Sunday. Filtering keeps only the
            // still-relevant sessions.
            try {
              if (new Date(e.end_at) <= nowDate) return false;
            } catch (_) {
              // if end_at is unparseable, keep the event visible
            }
            if (!filters.fromDate && !filters.toDate) return true;
            const s = e.start_at;
            if (filters.fromDate && s < filters.fromDate) return false;
            if (filters.toDate && s > filters.toDate + "T23:59:59") return false;
            return true;
          })
          .sort((a, b) => (a.start_at < b.start_at ? -1 : 1));
        if (evs.length === 0) continue; // skip no-event listings entirely
        const ev = evs[0];
        const dayKey = ev.start_at.slice(0, 10);
        if (!byDay.has(dayKey)) byDay.set(dayKey, []);
        byDay.get(dayKey).push({ ...oh, _displayEvent: ev, _allEvents: evs });
      }
      const dayGroups = Array.from(byDay.entries())
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([k, v]) => [k, v.sort((a, b) => {
          const sa = a._displayEvent.start_at, sb = b._displayEvent.start_at;
          if (sa !== sb) return sa < sb ? -1 : 1;
          return (b.price || 0) - (a.price || 0);
        })]);
      return { mode: "agenda", groups: dayGroups };
    }
    // Fallback: group by neighborhood (no schedule data available at all)
    const byHood = new Map();
    for (const oh of displayedOpenHouses) {
      const key = oh.neighborhood || "Other Saskatoon";
      if (!byHood.has(key)) byHood.set(key, []);
      byHood.get(key).push(oh);
    }
    for (const list of byHood.values()) {
      list.sort((a, b) => (b.price || 0) - (a.price || 0));
    }
    return {
      mode: "neighborhood",
      groups: Array.from(byHood.entries()).sort(([a], [b]) => (a < b ? -1 : 1)),
    };
  }, [displayedOpenHouses, listingTab, filters.fromDate, filters.toDate, nowTs]);

  // -- Actions --
  // The old "click a button, scan Saskatoon" flow is now a modal — the
  // button just opens the config dialog. `handleScanStarted` picks up
  // the job doc from the modal after the API kicks off the background
  // task so the "Scanning…" spinner + progress polling still work.
  const openScanModal = () => setScanModalOpen(true);
  const handleScanStarted = async (data) => {
    try {
      const { data: jobDoc } = await api.get(`/scrape-jobs/${data.job_id}`);
      setScanJob(jobDoc);
      setScanBusy(true);
    } catch { /* poller will pick it up */ }
  };

  // Deep-link from the Territory page: /open-houses?scan=territory&territory=<id>
  const [scanPreset, setScanPreset] = useState(null);
  useEffect(() => {
    if (searchParams.get("scan") !== "territory") return;
    setScanPreset({ mode: "territory", territoryId: searchParams.get("territory") || "" });
    setScanModalOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  // Geocoding is automatic — coords arrive with each listing; the post-scan
  // Nominatim backfill handles the rare miss. No manual trigger needed.

  const toggleVisited = async (oh) => {
    try {
      const { data } = await api.patch(`/openhouses/${oh.id}`, { visited: !oh.visited });
      setOpenHouses((rows) => rows.map((r) => (r.id === oh.id ? data : r)));
      toast.success(data.visited ? "Marked visited" : "Unmarked visited");
      loadStats();
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e) });
    }
  };

  const toggleSelection = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectionOrder((arr) => {
      if (arr.includes(id)) return arr.filter((x) => x !== id);
      return [...arr, id];
    });
    // Adding/removing a stop invalidates any prior optimization
    setDirectionsResult(null);
    setOptimizationState("none");
  };

  const clearSelection = () => {
    setSelected(new Set());
    setSelectionOrder([]);
    setDirectionsResult(null);
    setOptimizationState("none");
  };

  const selectAllInDay = (dayList) => {
    const ids = dayList.map((oh) => oh.id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    if (allSelected) {
      const drop = new Set(ids);
      setSelected((s) => new Set([...s].filter((id) => !drop.has(id))));
      setSelectionOrder((arr) => arr.filter((id) => !drop.has(id)));
    } else {
      setSelected((s) => {
        const next = new Set(s);
        ids.forEach((id) => next.add(id));
        return next;
      });
      setSelectionOrder((arr) => {
        const seen = new Set(arr);
        return [...arr, ...ids.filter((id) => !seen.has(id))];
      });
    }
    setDirectionsResult(null);
    setOptimizationState("none");
  };

  const reorderSelection = (fromIdx, toIdx) => {
    setSelectionOrder((arr) => {
      if (
        fromIdx < 0 ||
        toIdx < 0 ||
        fromIdx >= arr.length ||
        toIdx >= arr.length ||
        fromIdx === toIdx
      ) {
        return arr;
      }
      const next = [...arr];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
    // User intervention overrides any auto-optimization
    setOptimizationState((s) => (s === "optimized" ? "manual" : s));
  };

  // Applied when DirectionsService returns a successful optimization.
  // `orderedIds` is the new selectionOrder; `directions` is the full Google
  // response so the map can render the polyline.
  const applyOptimizedOrder = (orderedIds, directions) => {
    setSelectionOrder(orderedIds);
    setDirectionsResult(directions);
    setOptimizationState("optimized");
  };

  // Bulk mark all currently-selected stops as visited (after a trip is done).
  const markAllSelectedVisited = async () => {
    if (selectionOrder.length === 0) return;
    try {
      await Promise.all(
        selectionOrder.map((id) =>
          api.patch(`/openhouses/${id}`, { visited: true }).catch(() => null),
        ),
      );
      toast.success(`Marked ${selectionOrder.length} as visited`);
      clearSelection();
      await Promise.all([load(), loadStats()]);
    } catch (e) {
      toast.error("Bulk mark failed", { description: formatApiError(e) });
    }
  };

  const purgeNoEvents = async () => {
    setPurgeBusy(true);
    try {
      // Soft-delete by default + only purge listings >7 days old so a fresh
      // scan is never wiped. Items go to /recycle-bin for recovery.
      const { data } = await api.delete("/openhouses/no-events", {
        params: { older_than_days: 7, hard_delete: false },
      });
      toast.success(
        `${data.op === "hard-deleted" ? "Removed" : "Moved to Recycle Bin"}: ${data.deleted} stale listing${data.deleted === 1 ? "" : "s"}`,
        {
          description: data.older_than_days
            ? `Only listings last seen >${data.older_than_days} day${data.older_than_days === 1 ? "" : "s"} ago were affected.`
            : undefined,
        },
      );
      setPurgeOpen(false);
      // Clear any selections that referenced purged ids
      setSelected(new Set());
      setSelectionOrder([]);
      await Promise.all([load(), loadStats(), loadNoEventCount()]);
    } catch (e) {
      toast.error("Purge failed", { description: formatApiError(e) });
    } finally {
      setPurgeBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="openhouses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Phase 2 · Open House Scanner
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-display">
            Open Houses
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Realtor.ca open-house listings for Saskatoon, parsed into an agenda calendar.
            Select a few and tap <em>Build route</em> to open them as a Google Maps trip plan.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {noEventCount > 0 ? (
            <Button
              variant="outline"
              onClick={() => setPurgeOpen(true)}
              className="min-h-[44px] border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="purge-no-events-button"
              title="Remove listings whose open house is over"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove {noEventCount} stale
            </Button>
          ) : null}
          <Button
            onClick={openScanModal}
            disabled={scanBusy}
            className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="scan-openhouses-button"
          >
            {scanBusy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            {scanBusy ? "Scanning…" : "Scan Realtor.ca"}
          </Button>
        </div>
      </div>

      {/* View mode toggle (Agenda / Map) moved inline with the Current /
          Past listing tabs — keeps related view controls in one row. */}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <KpiTile label="Upcoming listings" value={currentListings.length} icon={Home} />
        <KpiTile label="Visited" value={stats.visited} accent="emerald" icon={CheckCircle2} />
      </div>

      {/* Per-day breakdown of the upcoming window */}
      {dayBreakdown.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-sm text-slate-600"
          data-testid="upcoming-day-breakdown"
        >
          {dayBreakdown.map((d, idx) => (
            <React.Fragment key={d.key}>
              {idx > 0 ? (
                <span aria-hidden className="text-slate-300 select-none">/</span>
              ) : null}
              <span
                className="inline-flex items-baseline gap-1.5"
                data-testid={`day-breakdown-${d.key}`}
              >
                <span
                  className={`font-medium ${
                    d.label === "Today" ? "text-amber-800" : "text-slate-700"
                  }`}
                >
                  {d.label}:
                </span>
                <span className="font-bold tabular-nums text-slate-900">{d.count}</span>
              </span>
            </React.Fragment>
          ))}
        </div>
      ) : null}

      {/* Job status */}
      {scanJob ? <ScanJobBanner job={scanJob} /> : null}

      {/* Filters */}
      <section
        className="rounded-lg border border-slate-200 bg-white p-4"
        data-testid="openhouses-filters"
      >
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-700">
          <Filter className="h-4 w-4" /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">From</Label>
            <Input
              type="date"
              value={filters.fromDate}
              onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
              className="min-h-[44px] mt-1"
              data-testid="filter-from-date"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">To</Label>
            <Input
              type="date"
              value={filters.toDate}
              onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
              className="min-h-[44px] mt-1"
              data-testid="filter-to-date"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Neighborhood</Label>
            <Select
              value={filters.neighborhood}
              onValueChange={(v) => setFilters((f) => ({ ...f, neighborhood: v }))}
            >
              <SelectTrigger className="min-h-[44px] mt-1" data-testid="filter-neighborhood">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All neighborhoods</SelectItem>
                {stats.neighborhoods.map((n) => (
                  <SelectItem key={n} value={n}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Search</Label>
            <Input
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Address, MLS, agent…"
              className="min-h-[44px] mt-1"
              data-testid="filter-search"
            />
          </div>
          <div className="flex flex-col gap-2 justify-end">
            <label className="flex items-center gap-2 text-sm text-slate-700 min-h-[44px]">
              <Checkbox
                checked={filters.hideVisited}
                onCheckedChange={(v) =>
                  setFilters((f) => ({ ...f, hideVisited: !!v }))
                }
                data-testid="filter-hide-visited"
              />
              <EyeOff className="h-3.5 w-3.5" /> Hide visited
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox
                checked={filters.onlyUnmet}
                onCheckedChange={(v) =>
                  setFilters((f) => ({ ...f, onlyUnmet: !!v }))
                }
                data-testid="filter-only-unmet"
              />
              <Tag className="h-3.5 w-3.5" /> Only un-met realtors
            </label>
          </div>
        </div>
      </section>

      {/* Sticky route chip — visible from anywhere on the page once >=1 stop
          is selected. Tap to open the route builder. */}
      {selectionOrder.length > 0 ? (
        <div
          className="sticky top-[4rem] md:top-2 z-20 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-2 flex flex-wrap items-center gap-2 shadow-md w-full sm:w-auto sm:inline-flex"
          data-testid="route-chip"
        >
          <Route className="h-4 w-4 text-emerald-700" />
          <span className="text-sm font-semibold text-emerald-900">
            Route: {selectionOrder.length} stop{selectionOrder.length === 1 ? "" : "s"} selected
          </span>
          <Button
            onClick={() => setRouteDialog(true)}
            className="text-white h-9 min-h-[36px] rounded-full"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="open-route-builder"
          >
            Build Route →
          </Button>
          <Button
            variant="ghost"
            onClick={clearSelection}
            className="h-9 min-h-[36px] rounded-full text-emerald-900 hover:bg-emerald-100"
            data-testid="clear-route-button"
          >
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
        </div>
      ) : null}

      {/* Current / Past listing tabs + inline Agenda/Map toggle */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-0 flex-wrap">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setListingTab("current")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              listingTab === "current"
                ? "border-[hsl(var(--brand))] text-[hsl(var(--brand))]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="oh-tab-current"
          >
            Current ({currentListings.length})
          </button>
          <button
            onClick={() => setListingTab("past")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              listingTab === "past"
                ? "border-[hsl(var(--brand))] text-[hsl(var(--brand))]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="oh-tab-past"
          >
            Past ({pastListings.length})
          </button>
        </div>
        <div
          className="inline-flex rounded-md border border-slate-200 bg-white p-1 shadow-sm mb-1"
          data-testid="view-mode-toggle"
          role="tablist"
        >
          <button
            type="button"
            onClick={() => setViewMode("agenda")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition min-h-[36px] ${
              viewMode === "agenda"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="view-mode-agenda"
            role="tab"
            aria-selected={viewMode === "agenda"}
          >
            <List className="h-4 w-4" /> Agenda
          </button>
          <button
            type="button"
            onClick={() => setViewMode("map")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition min-h-[36px] ${
              viewMode === "map"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="view-mode-map"
            role="tab"
            aria-selected={viewMode === "map"}
          >
            <MapIcon className="h-4 w-4" /> Map
          </button>
        </div>
      </div>

      {/* View body */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : viewMode === "map" ? (
        <MapDayScope
          mapDate={mapDate}
          onChange={setMapDate}
          openHouses={displayedOpenHouses}
          filteredCount={mapFilteredOpenHouses.length}
          fromDate={filters.fromDate}
          toDate={filters.toDate}
        >
          <OpenHouseMap
            openHouses={mapFilteredOpenHouses}
            selectedIds={selected}
            onToggleSelection={toggleSelection}
            onAreaSelect={(ids) => {
              setSelected((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.add(id));
                return next;
              });
              setSelectionOrder((prev) => {
                const newIds = ids.filter((id) => !prev.includes(id));
                return [...prev, ...newIds];
              });
              setDirectionsResult(null);
              setOptimizationState("none");
              toast.success(`Added ${ids.length} open house${ids.length === 1 ? "" : "s"} to route`);
            }}
            homeCoords={homeCoords}
            onOpenDetail={(id) => setDetailId(id)}
            directions={directionsResult}
          />
        </MapDayScope>
      ) : grouped.groups.length === 0 ? (
        <EmptyState onScan={openScanModal} scanBusy={scanBusy} />
      ) : (
        <>
          {grouped.mode === "neighborhood" ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              <strong>Schedule not auto-parsed for any listing.</strong> Realtor.ca only
              includes open-house times in the search response for listings happening within
              the next 24-48 hours. Listings here are grouped by neighborhood — click any row
              to see times on Realtor.ca.
            </div>
          ) : null}
          <div className="space-y-4">
            {grouped.groups.map(([key, items]) => (
              <DaySection
                key={key}
                mode={grouped.mode}
                groupKey={key}
                items={items}
                selectedIds={selected}
                onToggle={toggleSelection}
                onToggleVisited={toggleVisited}
                onOpenDetail={(id) => setDetailId(id)}
                onSelectAll={() => selectAllInDay(items)}
              />
            ))}
          </div>
        </>
      )}

      {/* Route Dialog */}
      <RouteDialog
        open={routeDialog}
        onClose={() => setRouteDialog(false)}
        selectedIds={selectionOrder}
        openHouses={openHouses}
        onReorder={reorderSelection}
        onRemoveStop={(id) => toggleSelection(id)}
        onMarkAllVisited={markAllSelectedVisited}
        optimizationState={optimizationState}
        onApplyOptimized={applyOptimizedOrder}
        directionsResult={directionsResult}
        homeCoords={homeCoords}
      />

      {/* Detail Dialog */}
      <OpenHouseDetailDialog
        id={detailId}
        onClose={() => setDetailId(null)}
        onUpdated={async () => {
          await load();
          await loadStats();
        }}
      />

      {/* Purge stale-listings confirm */}
      <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <AlertDialogContent data-testid="purge-no-events-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-amber-600" />
              Move stale open-house record{noEventCount === 1 ? "" : "s"} to Recycle Bin?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 space-y-2">
              <span className="block">
                These listings are still tagged as open houses on Realtor.ca but their
                schedule didn&apos;t come through. We&apos;ll <strong>only move ones last seen more
                than 7 days ago</strong> — fresh scans get a chance to fill the schedule via
                the detail-page pass first.
              </span>
              <span className="block">
                Items soft-delete to the <Link to="/recycle-bin" className="underline text-slate-900 font-medium">Recycle Bin</Link>, so
                a mistaken click is recoverable. <strong>Realtor cards stay intact</strong>.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={purgeBusy}
              data-testid="purge-cancel"
              className="min-h-[44px]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={purgeBusy}
              onClick={(e) => {
                e.preventDefault();
                purgeNoEvents();
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white min-h-[44px]"
              data-testid="purge-confirm"
            >
              {purgeBusy ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Move to Recycle Bin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* City-configurable scan modal — replaces the old "hardcoded
          Saskatoon" click behaviour. See ScanOpenHousesModal.jsx. */}
      <ScanOpenHousesModal
        open={scanModalOpen}
        onOpenChange={setScanModalOpen}
        onScanStarted={handleScanStarted}
        initialMode={scanPreset?.mode}
        initialTerritoryId={scanPreset?.territoryId}
      />
    </div>
  );
}


/**
 * MapDayScope — a chip row + native date input that scopes the Map view
 * to a single ISO date. Uses the same "Today / Tomorrow / weekday" labels
 * as the per-day breakdown so the two features feel consistent. Enumerates
 * chips from the pool of days that actually have events (max 7) rather than
 * a fixed range, so a Sunday scan doesn't show empty Mon-Sat chips.
 */
function MapDayScope({ mapDate, onChange, openHouses, filteredCount, fromDate, toDate, children }) {
  const dayChips = useMemo(() => {
    const dayCounts = new Map();
    for (const oh of openHouses) {
      const evs = Array.isArray(oh.events) ? oh.events : [];
      for (const ev of evs) {
        const d = (ev.start_at || "").slice(0, 10);
        if (!d) continue;
        // Optional guard: keep within the current filter's date range so
        // chips don't wander outside the user's active window.
        if (fromDate && d < fromDate) continue;
        if (toDate && d > toDate) continue;
        dayCounts.set(d, (dayCounts.get(d) || 0) + 1);
      }
    }
    const today = todayIso();
    const tomorrow = addDaysIso(today, 1);
    return Array.from(dayCounts.entries())
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .slice(0, 7)
      .map(([d, count]) => {
        let label;
        if (d === today) label = "Today";
        else if (d === tomorrow) label = "Tomorrow";
        else label = new Date(d + "T12:00:00").toLocaleDateString(undefined, {
          weekday: "short", month: "short", day: "numeric",
        });
        return { key: d, label, count };
      });
  }, [openHouses, fromDate, toDate]);

  // If the currently-selected day has no events, gently pull `mapDate`
  // to the first day that does (or clear it if the pool is empty).
  useEffect(() => {
    if (!mapDate) return;
    if (dayChips.length === 0) return;
    if (!dayChips.some((c) => c.key === mapDate)) {
      onChange(dayChips[0].key);
    }
  }, [dayChips, mapDate, onChange]);

  const activeChip = dayChips.find((c) => c.key === mapDate);
  const selectedLabel = activeChip
    ? activeChip.label
    : mapDate
    ? new Date(mapDate + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })
    : "All dates";

  return (
    <div className="space-y-3">
      <div
        className="rounded-md border border-slate-200 bg-white p-3 space-y-2"
        data-testid="map-day-scope"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-medium">
            Route day
          </div>
          <div className="text-xs text-slate-600">
            <strong className="text-slate-900">{filteredCount}</strong> pin
            {filteredCount === 1 ? "" : "s"} on {selectedLabel}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {dayChips.map((chip) => {
            const active = chip.key === mapDate;
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => onChange(chip.key)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`map-day-chip-${chip.key}`}
              >
                <span className="font-medium">{chip.label}</span>
                <span className={`text-[10px] ${active ? "text-slate-300" : "text-slate-400"}`}>
                  {chip.count}
                </span>
              </button>
            );
          })}
          <input
            type="date"
            value={mapDate || ""}
            onChange={(e) => onChange(e.target.value)}
            min={fromDate || undefined}
            max={toDate || undefined}
            className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
            data-testid="map-day-picker-input"
            title="Pick a specific date for the route"
          />
          {mapDate ? (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 px-1"
              data-testid="map-day-clear"
              title="Show every day in the current filter range"
            >
              All
            </button>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}
